import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Star, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useCart from '@/store/cartStore';
import useUserStore from '@/store/userStore';
import SignupModal from '@/components/SignupModal';

const Navbar = () => {
  const itemCount = useCart((state) => state.getItemCount());
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  
  const { user, isLoggedIn, logout } = useUserStore();

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
  };

  return (
    <>
      {/* Plant Perks Promo Banner */}
      {!isLoggedIn && (
        <div className="bg-gradient-to-r from-primary to-teal-600 text-white text-center py-2 px-4">
          <button 
            onClick={() => handleAuthClick('signup')}
            className="text-sm sm:text-base font-semibold hover:underline flex items-center justify-center gap-2 mx-auto"
          >
            <span>Sign up for <span className="font-bold text-amber-200">AquaPlants Plus Plant Perks</span> to start earning rewards on every purchase!</span>
            <span className="hidden sm:inline ml-1 bg-white/20 px-2 py-0.5 rounded-full text-xs">Join Free →</span>
          </button>
        </div>
      )}
      
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          {/* Logo Row */}
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-secondary rounded-lg transition-colors z-[60]"
              data-testid="menu-toggle"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="https://aquaplantsplus.square.site/uploads/b/92824204f5eb3a1741c28b0b59a1630abf175e712898d2f2d5a8e8dc409472cf/AquaPlantsPlusLogo_1762231535.jpeg"
                alt="AquaPlants Plus Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>

            <div className="flex items-center gap-2 md:gap-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61574799224266" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
                aria-label="Follow us on Facebook"
              >
                <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              {isLoggedIn ? (
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="hidden sm:flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                    <span>🌿</span>
                    <span>{user?.loyalty_points || 0} perks</span>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleAuthClick('login')}
                  className="hidden sm:flex text-sm"
                  data-testid="login-btn"
                >
                  Log In
                </Button>
              )}
              <Link to="/cart" className="relative" data-testid="cart-link">
                <Button variant="ghost" size="icon">
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                      {itemCount}
                    </span>
                  )}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Slide-out Menu */}
      <div 
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMenuOpen(false)}
      >
        <div className="absolute inset-0 bg-black/50" />
      </div>
      
      <div 
        className={`fixed left-0 bottom-0 w-72 bg-white border-r border-border shadow-xl z-40 transform transition-transform duration-300 overflow-y-auto ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ top: isLoggedIn ? '64px' : '100px' }}
        data-testid="slide-menu"
      >
        <div className="p-6 space-y-1">
          {/* User Status in Menu */}
          {isLoggedIn ? (
            <div className="mb-4 p-4 bg-gradient-to-r from-primary/10 to-green-50 rounded-lg">
              <p className="font-semibold text-sm truncate">{user?.name}</p>
              <div className="flex items-center gap-1 text-amber-600 mt-1">
                <span>🌿</span>
                <span className="text-sm font-medium">{user?.loyalty_points || 0} Plant Perks</span>
              </div>
              {user?.loyalty_points >= 100 && (
                <p className="text-xs text-green-600 mt-1">✓ $5 reward available!</p>
              )}
              <Button 
                size="sm" 
                variant="outline"
                className="w-full rounded-full text-sm mt-3"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-1" /> Log Out
              </Button>
            </div>
          ) : (
            <div className="mb-4 p-4 bg-gradient-to-r from-primary/10 to-green-50 rounded-lg space-y-2">
              <p className="font-semibold text-sm">Join & Save!</p>
              <Button 
                size="sm" 
                className="w-full rounded-full text-sm"
                onClick={() => { setMenuOpen(false); handleAuthClick('signup'); }}
              >
                Sign Up - Get 15% Off
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="w-full rounded-full text-sm"
                onClick={() => { setMenuOpen(false); handleAuthClick('login'); }}
              >
                Log In
              </Button>
            </div>
          )}

          <Link 
            to="/" 
            className="block py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-lg visited:text-foreground"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-home"
          >
            Home
          </Link>
          <Link 
            to="/shop" 
            className="block py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-lg visited:text-foreground"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-shop"
          >
            Shop
          </Link>
          <Link 
            to="/current-deals" 
            className="block py-3 px-4 text-primary font-semibold hover:bg-secondary rounded-lg transition-colors text-lg visited:text-primary"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-deals"
          >
            On Sale
          </Link>
          <Link 
            to="/shop?category=bundles" 
            className="block py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-lg visited:text-foreground"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-bundle"
          >
            Bundles
          </Link>
          <Link 
            to="/blog" 
            className="block py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-lg visited:text-foreground"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-blog"
          >
            Blog
          </Link>
          <Link 
            to="/shipping-policy" 
            className="block py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-lg visited:text-foreground"
            onClick={() => setMenuOpen(false)}
            data-testid="menu-shipping"
          >
            Shipping & Returns
          </Link>
          <a 
            href="mailto:contact.us@aquaplantsplus.com" 
            className="block py-3 px-4 text-foreground hover:bg-secondary rounded-lg transition-colors text-lg"
            data-testid="menu-contact"
          >
            Contact Us
          </a>
        </div>
      </div>

      <SignupModal open={showAuth} onOpenChange={setShowAuth} initialMode={authMode} />
    </>
  );
};

export default Navbar;
